import { useState } from "react";
//old import { Link, useNavigate } from "react-router-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.js";
import { clearCachedNotifications } from "../../hooks/useNotifications.js";
import api from "../../api/api.js";
import { FiEye, FiEyeOff, FiArrowLeft } from "react-icons/fi";
import { shouldForceOnboarding } from "../OnboardingGate";

// The page a signed-out visitor was sent here from (App.js passes it as state.from), e.g. a
// Licenses link in an email: signing in goes back to it. App.js's /login route reads it too.
export function pageBeforeSignIn(location) {
    const from = location?.state?.from;
    return from?.pathname && from.pathname !== "/login" ? `${from.pathname}${from.search || ""}${from.hash || ""}` : null;
}

// Utility function to wait for session cookie to be set
const waitForSessionCookie = async (maxAttempts = 5, delay = 300) => {
    // First, give the browser a moment to process the Set-Cookie header
    await new Promise(resolve => setTimeout(resolve, delay));
    
    // Then verify the cookie is working by making a test request
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            // Make a lightweight request to verify the session cookie is working
            // Use validateStatus to prevent axios from throwing on 401
            const testResponse = await api.get("/user/me", {
                validateStatus: (status) => status === 200 || status === 401
            });
            
            if (testResponse.status === 200) {
                // Cookie is set and working - session is valid
                return true;
            }
            
            // Cookie not ready yet (401), wait and retry
            if (attempt < maxAttempts - 1) {
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        } catch (error) {
            // Network errors or other issues - wait and retry
            if (attempt < maxAttempts - 1) {
                await new Promise(resolve => setTimeout(resolve, delay));
            } else {
                console.warn("Error verifying session cookie:", error);
            }
        }
    }
    
    // If we get here, cookie verification didn't succeed after max attempts
    // This could mean the cookie isn't set, but we'll proceed anyway
    // The redirect will trigger checkAuth which will handle authentication
    console.warn("Session cookie verification timed out, proceeding with redirect");
    return false;
};

// ACCEPT isDarkMode AS A PROP
export default function Login({ isDarkMode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [errors, setErrors] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { setUser } = useAuth();
    const [unverifiedEmail, setUnverifiedEmail] = useState(null);
    const [resendStatus, setResendStatus] = useState("");
    // The unverified account is a pending coach: verify and resend use the coach endpoints
    const [unverifiedIsCoach, setUnverifiedIsCoach] = useState(false);
    // ...or a pending organization's sign-up (its type): the org endpoints, and its own sign-up page takes the code
    const [unverifiedOrgType, setUnverifiedOrgType] = useState(null);

    const [loginStatus, setLoginStatus] = useState({loading: false, success: false, message: ''});
    const [emailTouched, setEmailTouched] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // Removed SQL injection check for password - passwords can contain special characters like !, @, #, etc.
    // Only check for truly dangerous SQL injection patterns in email

    const validate = (emailValue, passwordValue, submitAttempt = false, emailTouchedOverride = emailTouched) => {
        const newErrors = {};

        if (submitAttempt && !emailValue) {
            newErrors.email = "Email address is required.";
        }
        if (submitAttempt && !passwordValue) {
            newErrors.password = "Password is required.";
        }

        if (emailValue) {
            if (/\s/.test(emailValue)) {
                newErrors.email = "Email address cannot contain spaces.";
            }
            // Only show the format hint once the user has left the field (or tried to
            // submit) — flagging it on every keystroke was too noisy while typing.
            else if (!emailRegex.test(emailValue)) {
                if (submitAttempt || emailTouchedOverride) {
                    newErrors.email = "Please enter a valid email address format (e.g., user@domain.com).";
                }
            }
            // Only check for dangerous SQL patterns in email, not special characters
            //else if (/['";\\]/.test(emailValue)) {
            // Same rule as Signup (an apostrophe is allowed there, e.g. o'brien@...)
            else if (/[";\\]/.test(emailValue)) {
                newErrors.email = "Email contains potentially unsafe characters.";
            }
        }

        // Any password signup, reset and change-password accept must be typeable
        // here too (passwords are hashed and sent as parameters, never as SQL)
        //if (passwordValue) {
        //    // Allow special characters in passwords (like !, @, #, $, etc.)
        //    // Only block truly dangerous patterns
        //    if (/['";\\]/.test(passwordValue)) {
        //        newErrors.password = "Password contains unsafe characters.";
        //    }
        //}

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0; // Returns true if no errors
    };


    const handleEmailChange = (e) => {
        const value = e.target.value;
        if (value.includes(' ')) {
        }
        setEmail(value);
        if (isSubmitted || value) {
            validate(value, password, isSubmitted);
        }
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        if (isSubmitted || value) {
            validate(email, value, isSubmitted);
        }
    };
    const handleResendVerification = async () => {
        setResendStatus("sending");
        try {
            //old code
            //await api.post("/auth/resend-verification", { email: unverifiedEmail });
            //old await api.post(unverifiedIsCoach ? "/auth/signup/coach/resend" : "/auth/resend-verification", { email: unverifiedEmail });
            await api.post(unverifiedOrgType ? "/org/signup/resend"
                : unverifiedIsCoach ? "/auth/signup/coach/resend" : "/auth/resend-verification", { email: unverifiedEmail });
            setResendStatus("sent");
        } catch {
            setResendStatus("error");
        }
    };

    // 4. Form Submission Logic
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitted(true);
        setLoginStatus({ loading: true, success: false, message: "" });

        const isValid = validate(email, password, true);

        if (isValid) {
            try {
                const response = await api.post("/auth/login", {
                    email: email,
                    password: password
                });

                if (response.data.success) {
                    setLoginStatus({
                        loading: false,
                        success: true,
                        message: "Login Successful! Setting up session..."
                    });

                    // 1. Wait for the session cookie to be usable BEFORE exposing the
                    // user to the router: App.js redirects away from /login the moment
                    // `user` is set, and the dashboard's first API calls would race the
                    // cookie and 401-bounce back to /login.
                    await waitForSessionCookie();

                    // 2. Persist to localStorage so AuthProvider sees it on refresh.
                    // Clear any notifications cached by a previous account in this
                    // browser (e.g. someone who closed the tab instead of logging out)
                    // before this account's own alerts sync in.
                    clearCachedNotifications();
                    localStorage.setItem("user", JSON.stringify(response.data.user));

                    // 3. Update global state (this triggers the App.js redirect)
                    setUser(response.data.user);

                    setLoginStatus({
                        loading: false,
                        success: true,
                        message: "Login Successful! Redirecting..."
                    });

                    // 4. Redirect after cookie is confirmed
                    const u = response.data.user;
                    // back to the page they came for, unless a student's sign-up isn't finished (that comes first)
                    const backTo = pageBeforeSignIn(location);
                    //old if (u.isEmployer) {
                    if (backTo && !shouldForceOnboarding(u)) {
                        navigate(backTo, { replace: true });
                    } else if (u.isEmployer) {
                        navigate("/employer/dashboard", { replace: true });
                    } else if (u.isAdmin) {
                        navigate("/admin/dashboard", { replace: true });
                    } else if (u.isOrgAdmin) {
                        navigate("/org", { replace: true });
                    } else if (u.isCoach || u.isMentor) {
                        navigate("/coaching/dashboard", { replace: true });
                    } else if (u.emailVerified && u.profileComplete === false) {
                        navigate("/signup?stage=2&verified=1", { replace: true });
                    } else {
                        navigate("/userdashboard", { replace: true });
                    }
                }
                else if (response.status === 403 && response.data.code === "EMAIL_NOT_VERIFIED") {
                    setUnverifiedEmail(email);
                    setUnverifiedIsCoach(false);
                    setUnverifiedOrgType(null);
                    setLoginStatus({ loading: false, success: false, message: "" });
                }
                else {
                    setLoginStatus({ loading: false, success: false, message: response.data.error || "Login failed." });
                }
            } catch (err) {
                if (err.response?.status === 403 && err.response?.data?.code === "EMAIL_NOT_VERIFIED") {
                    setUnverifiedEmail(email);
                    setUnverifiedIsCoach(false);
                    setUnverifiedOrgType(null);
                    setLoginStatus({ loading: false, success: false, message: "" });
                } else if (err.response?.status === 403 && err.response?.data?.code === "COACH_EMAIL_NOT_VERIFIED") {
                    // Pending coach who never entered the emailed code
                    setUnverifiedEmail(err.response.data.email || email);
                    setUnverifiedIsCoach(true);
                    setUnverifiedOrgType(null);
                    setResendStatus("");
                    setLoginStatus({ loading: false, success: false, message: "" });
                } else if (err.response?.status === 403 && err.response?.data?.code === "ORG_EMAIL_NOT_VERIFIED") {
                    // An organization's sign-up whose emailed code was never entered: a new code goes
                    // out now (the first one has likely expired), then its sign-up page takes it
                    const orgEmail = err.response.data.email || email;
                    setUnverifiedEmail(orgEmail);
                    setUnverifiedIsCoach(false);
                    setUnverifiedOrgType(err.response.data.orgType || "education");
                    setResendStatus("sending");
                    setLoginStatus({ loading: false, success: false, message: "" });
                    api.post("/org/signup/resend", { email: orgEmail })
                        .then(() => setResendStatus("sent"))
                        .catch(() => setResendStatus("error"));
                } else {
                    let message = "Login failed. Please try again.";
                    if (err.code === "ECONNABORTED" || (err.message || "").toLowerCase().includes("timeout")) {
                        message = "The server took too long to respond. Try again, or check whether the API is up.";
                    } else if (!err.response) {
                        message =
                            "Could not complete sign-in (network or browser blocked the response). " +
                            "Try another browser, disable content blockers for this page, and ensure the " +
                            "front-end origin is in backend CORS (FRONTEND_APP_URL or CORS_EXTRA_ORIGINS on the API).";
                    } else {
                        const serverMsg = err.response.data?.error;
                        if (serverMsg) message = serverMsg;
                    }
                    setLoginStatus({ loading: false, success: false, message });
                }
            }
        } else {
            setLoginStatus({ loading: false, success: false, message: "" });
        }
    };

    return (
        <div className={`min-h-screen transition-colors duration-500 ${
            isDarkMode
                ? 'bg-slate-950 bg-[radial-gradient(ellipse_at_top,_var(--tw-color-slate-800)_10%,_var(--tw-color-slate-950))]'
                : 'bg-white bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-color-slate-200)_0%,_var(--tw-color-white))]'
        }`}>
            <div className="flex items-center justify-center min-h-screen px-4 pb-12">
                <div className={`w-full max-w-md rounded-3xl p-10 shadow-xl backdrop-blur-sm transition-all duration-300 ${
                    isDarkMode
                        ? 'bg-slate-900/80 shadow-slate-900/50'
                        : 'border border-slate-100 bg-white/90 shadow-slate-300/50'
                }`}>
                    {/* A way back to the landing page for anyone who opened sign in
                        by mistake — same control as the first step of signup. */}
                    <Link
                        to="/"
                        aria-label="Back to home"
                        title="Back to home"
                        className={`inline-flex items-center justify-center w-10 h-10 mb-6 rounded-full border transition-colors ${
                            isDarkMode
                                ? 'border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-200 hover:border-slate-600'
                                : 'border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 hover:border-slate-300'
                        }`}
                    >
                        <FiArrowLeft size={18} />
                    </Link>
                    <h2 className={`text-3xl font-extrabold text-center mb-8 ${
                        isDarkMode ? 'text-slate-50' : 'text-slate-900'
                    }`}>
                        Sign In
                    </h2>

                    {loginStatus.message && (
                        <div className={`mb-6 p-4 rounded-xl text-center text-sm font-bold animate-pulse ${
                            loginStatus.success ? 'bg-primary-500/20 text-primary-500' : 'bg-red-500/20 text-red-500'
                        }`}>
                            {loginStatus.message}
                        </div>
                    )}
                    {unverifiedEmail && (
                        <div className={`mb-6 p-4 rounded-xl text-sm border ${
                            isDarkMode
                                ? 'bg-yellow-900/20 border-yellow-700/40 text-yellow-300'
                                : 'bg-yellow-50 border-yellow-200 text-yellow-800'
                        }`}>
                            <p className="font-semibold mb-1">📧 Please verify your email</p>
                            <p className="mb-3 text-xs">
                                Check your inbox for the 6-digit verification code sent to <span className="font-mono font-bold">{unverifiedEmail}</span>.
                            </p>
                            <button
                                type="button"
                                //old code
                                //onClick={() => navigate(`/verify-email?email=${encodeURIComponent(unverifiedEmail)}`)}
                                //old onClick={() => navigate(`/verify-email?email=${encodeURIComponent(unverifiedEmail)}${unverifiedIsCoach ? "&type=coach" : ""}`)}
                                onClick={() => navigate(unverifiedOrgType
                                    ? `${unverifiedOrgType === "nonprofit" ? "/community-partners/signup" : "/organizations/signup"}?verify=${encodeURIComponent(unverifiedEmail)}`
                                    : `/verify-email?email=${encodeURIComponent(unverifiedEmail)}${unverifiedIsCoach ? "&type=coach" : ""}`)}
                                className="text-xs font-semibold text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors mr-4"
                            >
                                Enter your verification code →
                            </button>
                            {resendStatus === "sent" ? (
                                <p className="text-primary-600 font-semibold text-xs">✓ Verification code resent!</p>
                            ) : resendStatus === "error" ? (
                                <p className="text-red-500 text-xs">Failed to resend. Please try again.</p>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleResendVerification}
                                    disabled={resendStatus === "sending"}
                                    className="text-xs font-semibold text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors"
                                >
                                    {resendStatus === "sending" ? "Sending..." : "Resend verification code"}
                                </button>
                            )}
                        </div>
                    )}

                    <form className="space-y-4" onSubmit={handleSubmit}>
                        {/* --- Email Field Group --- */}
                        <div>
                            <input
                                type="text" // Changed to 'text' to better control input, though 'email' is valid too
                                placeholder="Email address"
                                value={email}
                                onChange={handleEmailChange}
                                onBlur={() => {
                                    setEmailTouched(true);
                                    validate(email, password, isSubmitted, true);
                                }}
                                required
                                className={`w-full rounded-xl border px-4 py-3 text-sm transition-all duration-300 focus:outline-none focus:ring-4 ${
                                    errors.email
                                        ? 'border-red-500 focus:ring-red-400/50'
                                        : 'focus:ring-primary-400/50'
                                } ${
                                    isDarkMode
                                        ? 'border-slate-700 bg-slate-800 text-slate-100 placeholder-slate-400 focus:border-primary-500'
                                        : 'border-slate-300 bg-slate-50 text-slate-900 placeholder-slate-500 focus:border-primary-500'
                                }`}
                            />
                            {/* Inline Error Message for Email */}
                            {errors.email && (
                                <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.email}</p>
                            )}
                        </div>

                        {/* --- Password Field Group --- */}
                        <div>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Password"
                                    value={password}
                                    onChange={handlePasswordChange}
                                    required
                                    className={`w-full rounded-xl border px-4 py-3 pr-11 text-sm transition-all duration-300 focus:outline-none focus:ring-4 ${
                                        errors.password
                                            ? 'border-red-500 focus:ring-red-400/50'
                                            : 'focus:ring-primary-400/50'
                                    } ${
                                        isDarkMode
                                            ? 'border-slate-700 bg-slate-800 text-slate-100 placeholder-slate-400 focus:border-primary-500'
                                            : 'border-slate-300 bg-slate-50 text-slate-900 placeholder-slate-500 focus:border-primary-500'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${
                                        isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'
                                    }`}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                </button>
                            </div>

                            {/* Inline Error Message for Password (e.g., unsafe characters) */}
                            {errors.password && (
                                <p className="mt-1 ml-1 text-xs text-red-500 font-medium">{errors.password}</p>
                            )}

                            <div className="mt-2 text-right">
                                <Link
                                    to="/forgot-password"
                                    className={`text-xs font-semibold transition-colors ${
                                        isDarkMode ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-700'
                                    }`}
                                >
                                    Forgot password?
                                </Link>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="w-full rounded-xl bg-primary-600 px-4 py-3 text-base font-semibold text-white shadow-lg shadow-primary-500/30 hover:bg-primary-700 transition-all duration-300 active:scale-[0.98]"
                        >
                            {loginStatus.loading ? "Authenticating..." : "Secure Login"}
                        </button>
                    </form>

                    <p className={`text-center text-sm mt-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                        Don't have an account?{" "}
                        <Link
                            to="/signup"
                            className="font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                        >
                            Create an account
                        </Link>
                    </p>

                </div>
            </div>
        </div>
    );
}